import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Warning, PencilSimple, Check, X, Clock, User, ArrowRight, Plus } from "@phosphor-icons/react";

export const patientIssue = (p) => (p?.issue || "").trim() || (p?.observations || "").trim();
export const daysSince = (iso) => (iso ? Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 86400000)) : 0);
const DAMAGE_LABEL = { golpe: "Golpe", rayon: "Rayón", pantalla_rota: "Pantalla rota", faltante: "Faltante", otro: "Otro" };

function Meta({ label, children, testid }) {
  return (
    <div className="min-w-0" data-testid={testid}>
      <div className="text-[9px] uppercase tracking-widest text-zinc-500 font-semibold">{label}</div>
      <div className="mt-0.5">{children}</div>
    </div>
  );
}

// Bloque principal del expediente: por qué está aquí + estado + días + técnico.
export default function PatientIssueBlock({ patient, stateBadge, canClaim, onClaim, busy, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const issue = (patient.issue || "").trim();
  const fromObs = !issue && (patient.observations || "").trim();
  const text = issue || fromObs;
  const days = daysSince(patient.received_at);
  const damages = patient.damages || [];
  useEffect(() => { setEditing(false); }, [patient.id]);

  const startEdit = () => { setDraft(text); setEditing(true); };
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/patients/${patient.id}/issue`, { issue: draft.trim() });
      toast.success("Problema actualizado");
      setEditing(false);
      onChanged?.(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo guardar");
    } finally { setSaving(false); }
  };

  const daysTone = days >= 30 ? "text-[#E0000F]" : days >= 14 ? "text-amber-600" : "text-zinc-900";

  return (
    <section className="rounded-md border border-amber-200 border-l-4 border-l-[#E0000F] bg-amber-50/60 p-4" data-testid="patient-issue-block">
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <div className="text-[10px] uppercase tracking-widest font-bold text-amber-800 inline-flex items-center gap-1">
              <Warning size={12} weight="fill" className="text-[#E0000F]" /> ¿Por qué está aquí?
            </div>
            {!editing && (
              <button type="button" onClick={startEdit} className="text-[10px] uppercase tracking-widest font-semibold text-zinc-500 hover:text-[#002FA7] inline-flex items-center gap-1" data-testid="patient-issue-edit">
                {text ? <PencilSimple size={11} weight="bold" /> : <Plus size={11} weight="bold" />} {text ? "Editar" : "Agregar"}
              </button>
            )}
          </div>

          {editing ? (
            <div className="mt-2 space-y-2" data-testid="patient-issue-editor">
              <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                rows={3}
                autoFocus
                placeholder="Ej: No enciende; le falta disipador y procesador; pantalla con líneas."
                className="w-full text-sm border border-amber-300 bg-white rounded-md p-2 focus:outline-none focus:border-[#002FA7] resize-none"
                data-testid="patient-issue-input"
              />
              <div className="flex gap-2 justify-end">
                <Button size="sm" variant="outline" onClick={() => setEditing(false)} disabled={saving} className="h-7 gap-1" data-testid="patient-issue-cancel"><X size={12} /> Cancelar</Button>
                <Button size="sm" onClick={save} disabled={saving} className="h-7 gap-1 bg-[#002FA7] hover:bg-[#002277] text-white" data-testid="patient-issue-save"><Check size={12} weight="bold" /> {saving ? "Guardando…" : "Guardar"}</Button>
              </div>
            </div>
          ) : text ? (
            <>
              <p className="mt-1.5 text-base sm:text-lg font-semibold text-zinc-900 leading-snug whitespace-pre-line" data-testid="patient-issue-text">{text}</p>
              {fromObs && <div className="text-[10px] text-zinc-500 italic mt-1" data-testid="patient-issue-from-obs">Tomado de observaciones · edítalo para dejarlo como problema</div>}
            </>
          ) : (
            <button type="button" onClick={startEdit} className="mt-1.5 w-full text-left text-sm text-zinc-500 italic border border-dashed border-amber-300 rounded-md px-3 py-2 hover:border-[#002FA7] hover:text-[#002FA7]" data-testid="patient-issue-empty">
              Sin problema registrado — escribe la falla, los faltantes o el motivo de ingreso.
            </button>
          )}

          {damages.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2.5" data-testid="patient-issue-damages">
              {damages.map((d, i) => (
                <span key={i} className="text-[11px] bg-white text-amber-900 border border-amber-300 rounded-sm px-2 py-0.5">
                  <b>{DAMAGE_LABEL[d.kind] || d.kind}</b>{d.description ? ` — ${d.description}` : ""}
                </span>
              ))}
            </div>
          )}
        </div>

        <div className="sm:w-44 shrink-0 grid grid-cols-3 sm:grid-cols-1 gap-3 sm:border-l sm:border-amber-200 sm:pl-4">
          <Meta label="En hospital" testid="patient-issue-days">
            <div className={`font-heading font-extrabold text-2xl leading-none numeric ${daysTone}`}>{days}<span className="text-xs font-semibold text-zinc-500 ml-1">{days === 1 ? "día" : "días"}</span></div>
            <div className="text-[10px] text-zinc-500 inline-flex items-center gap-1 mt-0.5"><Clock size={9} /> desde {patient.received_at ? new Date(patient.received_at).toLocaleDateString("es-MX", { day: "2-digit", month: "short" }) : "—"}</div>
          </Meta>
          <Meta label="Estado" testid="patient-issue-state">{stateBadge}</Meta>
          <Meta label="Técnico" testid="patient-issue-tech">
            {patient.technician_name ? (
              <div className="text-sm font-semibold text-zinc-900 inline-flex items-center gap-1 truncate"><User size={12} weight="bold" className="text-[#002FA7] shrink-0" /> {patient.technician_name}</div>
            ) : canClaim ? (
              <Button size="sm" onClick={onClaim} disabled={busy} className="h-7 bg-[#002FA7] hover:bg-[#0043c2] text-white gap-1 text-xs" data-testid="patient-detail-claim">
                <ArrowRight size={12} weight="bold" /> Tomar
              </Button>
            ) : (
              <span className="text-sm text-zinc-400 italic">Sin asignar</span>
            )}
          </Meta>
        </div>
      </div>
    </section>
  );
}
